import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, TableShell, Field, Drawer } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const empty = { first_name: "", last_name: "", date_of_birth: "", phone: "", email: "", address: "", mobility: "ambulatory", medicaid_id: "", con_number: "", notes: "", company_id: "" };
const MOBILITY = [
  { value: "ambulatory", label: "Ambulatory" },
  { value: "wheelchair", label: "Wheelchair" },
  { value: "stretcher", label: "Stretcher" },
];
const HISTORY_TRUNCATE = 80;
const fmtDeletedDate = (iso) => (iso ? String(iso).slice(0, 10) : "");

function fmtWhen(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "agency_admin";
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState({});
  const [showDeleted, setShowDeleted] = useState(false);

  const load = () => api.get(`/customers${showDeleted ? "?include_deleted=true" : ""}`).then((r) => setRows(r.data));
  useEffect(() => {
    load();
    if (user?.role !== "client_user") api.get("/companies").then((r) => setCompanies(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, showDeleted]);

  const restore = async (id) => {
    try {
      await api.post(`/customers/${id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  const save = async () => {
    try {
      const p = { ...form, date_of_birth: form.date_of_birth || null, company_id: form.company_id || null };
      if (editingId) await api.put(`/customers/${editingId}`, p);
      else await api.post("/customers", p);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openHistory = (c) => {
    setHistoryCustomer(c);
    setHistoryOpen(true);
    setHistoryExpanded({});
    setHistoryLoading(true);
    api.get(`/customers/${c.id}/audit`)
      .then((r) => setHistoryRows(r.data?.rows || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setHistoryLoading(false));
  };

  const toggleHistoryExpanded = (id) => setHistoryExpanded((e) => ({ ...e, [id]: !e[id] }));

  const companyOpts = [{ value: "", label: "— None —" }, ...companies.map((c) => ({ value: c.id, label: c.name }))];

  return (
    <div data-testid="customers-page">
      <PageHeader
        subtitle="Data"
        title="Customers"
        action={
          <div className="flex items-center gap-4">
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} data-testid="customers-show-deleted" />
                Show deleted
              </label>
            )}
            <button onClick={() => { setForm(empty); setEditingId(null); setOpen(true); }} className="btn-primary" data-testid="add-customer-btn">+ Add Customer</button>
          </div>
        }
      />
      <TableShell columns={["Name", "DOB", "Mobility", "Company", "Medicaid", "Actions"]} empty={rows.length === 0 ? "No customers yet." : null}>
        {rows.map((c) => {
          const isDeleted = !!c.deleted_at;
          return (
            <tr key={c.id} className={`tbl-row border-t border-slate-100 ${isDeleted ? "opacity-60" : ""}`}>
              <td className="px-4 py-2.5 font-medium">
                {c.first_name} {c.last_name}
                <div className="text-slate-500 text-[11px]">{c.phone}</div>
                {isDeleted && (
                  <span className="mt-1 inline-block bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 text-[10px]">
                    deleted {fmtDeletedDate(c.deleted_at)}
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 font-mono text-xs">{c.date_of_birth || "-"}</td>
              <td className="px-4 py-2.5 capitalize">{c.mobility}</td>
              <td className="px-4 py-2.5 text-slate-600">{user?.role === "client_user" ? "-" : companyName(c.company_id)}</td>
              <td className="px-4 py-2.5 font-mono text-xs">{c.medicaid_id || "-"}</td>
              <td className="px-4 py-2.5 space-x-3 whitespace-nowrap">
                {isDeleted ? (
                  <button className="text-emerald-700 hover:underline text-xs" onClick={() => restore(c.id)} data-testid={`restore-${c.id}`}>Restore</button>
                ) : (
                  <>
                    <button className="text-[#005E82] hover:underline text-xs" onClick={() => { setForm({ ...empty, ...c }); setEditingId(c.id); setOpen(true); }}>Edit</button>
                    <button className="text-slate-600 hover:underline text-xs" onClick={() => openHistory(c)} data-testid={`history-${c.id}`}>History</button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </TableShell>

      <Drawer open={open} onClose={() => setOpen(false)} title={editingId ? "Edit Customer" : "New Customer"} footer={<>
        <button onClick={() => setOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
        <button onClick={save} className="btn-primary" data-testid="save-customer-btn">Save</button>
      </>}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" testId="c-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            <Field label="Last name" testId="c-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date of birth" testId="c-dob" type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
            <Field label="Phone" testId="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <Field label="Email (for rating link)" testId="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="rider@example.com" />
          <Field label="Address" testId="c-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mobility" testId="c-mobility" value={form.mobility} onChange={(e) => setForm({ ...form, mobility: e.target.value })} options={MOBILITY} />
            <Field label="Medicaid ID" testId="c-medicaid" value={form.medicaid_id} onChange={(e) => setForm({ ...form, medicaid_id: e.target.value })} />
          </div>
          <Field
            label="CON # (payer client number)"
            testId="c-con-number"
            value={form.con_number}
            onChange={(e) => setForm({ ...form, con_number: e.target.value })}
            placeholder="Optional, from a payer export"
          />
          {user?.role !== "client_user" && (
            <Field label="Client company" testId="c-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} options={companyOpts} />
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
              className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300" />
          </div>
        </div>
      </Drawer>

      <Drawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title="Record history"
      >
        <div className="space-y-3">
          <div className="text-xs text-slate-500">
            {historyCustomer ? `${historyCustomer.first_name} ${historyCustomer.last_name}` : ""}
          </div>
          {historyLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : historyRows.length === 0 ? (
            <div className="empty">No history for this customer yet.</div>
          ) : (
            <div className="space-y-2">
              {historyRows.map((h) => {
                const detailsStr = JSON.stringify(h.details || {});
                const isLong = detailsStr.length > HISTORY_TRUNCATE;
                const isOpen = !!historyExpanded[h.id];
                const shown = isOpen || !isLong ? detailsStr : `${detailsStr.slice(0, HISTORY_TRUNCATE)}…`;
                return (
                  <div key={h.id} className="border border-slate-200 rounded-md p-3" data-testid={`history-row-${h.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs text-slate-500">{fmtWhen(h.at)}</span>
                      <span className="inline-flex items-center text-[11px] font-mono px-2 py-0.5 rounded-full border bg-sky-50 text-sky-800 border-sky-200">
                        {h.action}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600 mt-1">{h.user_email || <span className="text-slate-400">-</span>}</div>
                    <div className="text-xs font-mono text-slate-500 mt-1 break-all">
                      {shown}
                      {isLong && (
                        <button onClick={() => toggleHistoryExpanded(h.id)} className="ml-1.5 text-[#005E82] text-xs hover:underline">
                          {isOpen ? "less" : "more"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
}
